/**
 * ClassList - Sidebar list of classes
 *
 * Displays all classes with selection state.
 * Includes add class button.
 */

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Plus, Users, GraduationCap } from "lucide-react";
import { useLocalDbStore } from "@/store/localDbStore";

interface ClassListProps {
  onAddClass: () => void;
}

export function ClassList({ onAddClass }: ClassListProps) {
  const { classes, selectedClassId, selectClass, isConnected, students } = useLocalDbStore();

  if (!isConnected) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Connect a database to view classes</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
          Classes
        </h3>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {classes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No classes yet
            </p>
          ) : (
            classes.map((cls) => {
              const isSelected = cls.id === selectedClassId;
              const studentCount = isSelected ? students.length : null;

              return (
                <button
                  key={cls.id}
                  onClick={() => selectClass(cls.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors",
                    "hover:bg-muted/50",
                    isSelected && "bg-primary/10 text-primary"
                  )}
                >
                  <Users className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{cls.name}</p>
                    {cls.grade && (
                      <p className="text-xs text-muted-foreground">
                        Grade {cls.grade}
                        {studentCount !== null && ` • ${studentCount} students`}
                      </p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>

      <div className="p-3 border-t">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onAddClass}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Class
        </Button>
      </div>
    </div>
  );
}
